import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Check, Copy, Plus, Save, Sparkles, Trash2 } from 'lucide-react'
import { clients, type ClientSettings } from '../../api'

/**
 * AI configuration — Studio surface.
 *
 * Why a Studio page instead of /settings: superusers (and anyone with
 * Studio access) need to inspect and edit per-client AI knobs across
 * tenants. The /settings page is the user's OWN-TENANT view; this is
 * the cross-tenant operator view.
 *
 * Edits:  `ai_auto_folder_enabled` (toggle on/off auto-foldering)
 * Read-only:  Client.context — the firm-level JSON blob exposed as
 *             {{client_context}} to every prompt. Surfaced here so
 *             operators can see exactly what context every prompt
 *             receives without poking the DB.
 *
 * `ai_domain_context` is hidden — its only consumer was the legacy
 * standalone auto_folder task. The in-DAG file_matter_into_folders
 * step does its own foldering using a dedicated prompt + the
 * folder_tree, current_filings, and global_filing_rules built-ins.
 * Field kept in the model for now in case we re-introduce it.
 */
const SELECTED_CLIENT_KEY = 'ai_config_selected_client_code'

export default function AIConfig() {
  const list = useQuery({ queryKey: ['clients'], queryFn: () => clients.list() })
  const allClients = list.data ?? []
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Resolve the initial selection in this priority order:
  //   1. Last client the user chose here, persisted by code (stable
  //      across deploys + tenant id renumbering)
  //   2. MOVU, the primary live tenant — sensible default while we have
  //      a single real client and a handful of demo/test ones
  //   3. First client in the list (alphabetical-ish from the API)
  // Runs once the list loads; subsequent picks come from the dropdown.
  useEffect(() => {
    if (selectedId != null || allClients.length === 0) return
    const savedCode = localStorage.getItem(SELECTED_CLIENT_KEY)
    const fromSaved = savedCode ? allClients.find((c) => c.code === savedCode) : null
    const fromDefault = allClients.find((c) => c.code === 'MOVU')
    const fallback = allClients[0]
    setSelectedId((fromSaved ?? fromDefault ?? fallback).id)
  }, [allClients, selectedId])

  const selected = useMemo(
    () => allClients.find((c) => c.id === selectedId) ?? null,
    [allClients, selectedId],
  )

  // Persist on every change so a new tab / refresh lands on the same
  // tenant. Stored as `code` rather than id — id can shift between
  // databases, code is stable.
  const onPick = (id: number) => {
    setSelectedId(id)
    const c = allClients.find((c) => c.id === id)
    if (c) localStorage.setItem(SELECTED_CLIENT_KEY, c.code)
  }

  return (
    <div className="me-scroll h-full overflow-auto">
      <div className="mx-auto max-w-3xl p-6">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="me-text-mute" />
        <h1 className="text-[18px] font-semibold me-text">AI configuration</h1>
      </div>
      <div className="mt-1 text-[12px] me-text-mute">
        Per-client AI knobs and a read-only view of the firm context
        that every prompt receives. Visible to operators with Studio access.
      </div>

      <div className="mt-6 me-card p-5">
        <div className="me-section-h">Client</div>
        <div className="text-[12px] me-text-mute mb-3">
          Pick which tenant's AI auto-folder config to view or edit.
        </div>
        <select
          value={selectedId ?? ''}
          onChange={(e) => onPick(Number(e.target.value))}
          className="me-input"
          disabled={list.isLoading}
        >
          {allClients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </div>

      {selected && <ClientAIConfigForm client={selected} />}

      {list.isLoading && (
        <div className="mt-6 me-text-mute text-[12px]">Loading clients…</div>
      )}
      </div>
    </div>
  )
}

function ClientAIConfigForm({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const [enabled, setEnabled] = useState<boolean>(client.ai_auto_folder_enabled ?? false)

  // Reset state when the client selection changes.
  useEffect(() => {
    setEnabled(client.ai_auto_folder_enabled ?? false)
  }, [client.id, client.ai_auto_folder_enabled])

  const save = useMutation({
    mutationFn: () => clients.update(client.id, {
      ai_auto_folder_enabled: enabled,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const isDirty = enabled !== (client.ai_auto_folder_enabled ?? false)

  return (
    <>
      <div className="mt-6 me-card p-5">
        <div className="me-section-h">Enable / disable</div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-0.5 cursor-pointer"
          />
          <div>
            <div className="font-medium me-text">
              Run AI auto-folder for <span className="me-mono">{client.code}</span>
            </div>
            <div className="text-[11.5px] me-text-mute leading-snug">
              When enabled, every matter touched by artifact processing gets
              evaluated by the AI for folder placement (subject to per-matter
              idempotency — unchanged matters are skipped). User-assigned
              folders are never overridden.
              {!enabled && (
                <span className="block mt-1 text-[color:var(--me-text)]">
                  Currently OFF — no AI filing for this tenant.
                </span>
              )}
            </div>
          </div>
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending}
          className={isDirty ? 'me-btn me-btn-primary' : 'me-btn'}
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {isDirty && (
          <button
            onClick={() => setEnabled(client.ai_auto_folder_enabled ?? false)}
            className="me-btn"
            disabled={save.isPending}
          >
            Discard
          </button>
        )}
        {save.isSuccess && !isDirty && (
          <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>
        )}
        {save.error && (
          <span className="text-[12px] text-[color:var(--me-urgent)]">
            {(save.error as Error).message}
          </span>
        )}
      </div>

      <ClientContextPanel client={client} />
    </>
  )
}

// ─── Editable client_context panel ────────────────────────────────────────
//
// Structured editor for the four commonly-used keys of Client.context
// (firm_name / description / colleagues / notes) plus an "Other fields"
// key-value escape hatch for anything else. Shows a live JSON preview
// of what will be sent to prompts as {{client_context}}. Saving POSTs
// the rebuilt JSON to /clients/{id}/.

type Colleague = { name: string; email: string; role: string; notes: string }
type ExtraField = { key: string; value: string }

const KNOWN_KEYS = new Set(['firm_name', 'description', 'colleagues', 'notes'])

// Pull the known keys out of Client.context into structured state; whatever's
// left becomes an "extras" list of key/value pairs the user can edit.
function splitContext(ctx: Record<string, unknown>): {
  firmName: string
  description: string
  colleagues: Colleague[]
  notes: string
  extras: ExtraField[]
} {
  const colleaguesIn = Array.isArray(ctx.colleagues) ? ctx.colleagues : []
  const colleagues: Colleague[] = colleaguesIn.map((c) => {
    const o = (typeof c === 'object' && c) ? (c as Record<string, unknown>) : {}
    return {
      name: typeof o.name === 'string' ? o.name : '',
      email: typeof o.email === 'string' ? o.email : '',
      role: typeof o.role === 'string' ? o.role : '',
      notes: typeof o.notes === 'string' ? o.notes : '',
    }
  })
  const extras: ExtraField[] = []
  for (const [k, v] of Object.entries(ctx)) {
    if (KNOWN_KEYS.has(k)) continue
    // Non-string extras get JSON-stringified into the textbox; the user
    // can edit it as text and we'll try to parse on save (falling back
    // to the raw string if it doesn't parse).
    const sv = typeof v === 'string' ? v : JSON.stringify(v)
    extras.push({ key: k, value: sv })
  }
  return {
    firmName: typeof ctx.firm_name === 'string' ? ctx.firm_name : '',
    description: typeof ctx.description === 'string' ? ctx.description : '',
    colleagues,
    notes: typeof ctx.notes === 'string' ? ctx.notes : '',
    extras,
  }
}

// Rebuild the JSON object from structured state. Empty known keys are
// included as '' (so the model still sees the key, just blank) UNLESS
// every structured field is empty, in which case we omit it entirely
// to keep the stored JSON tidy. Extras whose key is blank are dropped.
function buildContext(s: {
  firmName: string; description: string; colleagues: Colleague[];
  notes: string; extras: ExtraField[]
}): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (s.firmName.trim()) out.firm_name = s.firmName
  if (s.description.trim()) out.description = s.description
  // Drop entirely-empty colleague rows so we don't bloat the JSON with
  // {name:'',email:'',role:'',notes:''} placeholders the user added
  // and then never filled in.
  const cleanColleagues = s.colleagues.filter(
    (c) => c.name.trim() || c.email.trim() || c.role.trim() || c.notes.trim()
  )
  if (cleanColleagues.length > 0) out.colleagues = cleanColleagues
  if (s.notes.trim()) out.notes = s.notes
  for (const e of s.extras) {
    const k = e.key.trim()
    if (!k) continue
    // Try to parse as JSON so the user can put objects/arrays/numbers in.
    // If parsing fails, fall back to the raw string — which is the
    // common case for plain text values.
    let parsed: unknown = e.value
    try { parsed = JSON.parse(e.value) } catch { /* keep string */ }
    out[k] = parsed
  }
  return out
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Recursively sort object keys so two semantically-equal JSON values
// compare equal as strings. Without this, MOVU's saved context (keys in
// insertion order `notes, firm_name, colleagues, description`) doesn't
// match what buildContext emits (`firm_name, description, colleagues,
// notes`) and the dirty check fires immediately on load.
function canonicalize(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonicalize)
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(v as object).sort()) {
      out[k] = canonicalize((v as Record<string, unknown>)[k])
    }
    return out
  }
  return v
}

function ClientContextPanel({ client }: { client: ClientSettings }) {
  const qc = useQueryClient()
  const initial = useMemo(
    () => splitContext((client.context ?? {}) as Record<string, unknown>),
    [client.context]
  )
  const [firmName, setFirmName] = useState(initial.firmName)
  const [description, setDescription] = useState(initial.description)
  const [colleagues, setColleagues] = useState<Colleague[]>(initial.colleagues)
  const [notes, setNotes] = useState(initial.notes)
  const [extras, setExtras] = useState<ExtraField[]>(initial.extras)

  // Reset when the client selection changes.
  useEffect(() => {
    setFirmName(initial.firmName)
    setDescription(initial.description)
    setColleagues(initial.colleagues)
    setNotes(initial.notes)
    setExtras(initial.extras)
  }, [client.id, initial])

  const built = useMemo(
    () => buildContext({ firmName, description, colleagues, notes, extras }),
    [firmName, description, colleagues, notes, extras]
  )
  const preview = JSON.stringify(built, null, 2)

  // Light realtime validation. Emails must look like emails (or be
  // blank — partly-filled rows shouldn't block save). Extra-field keys
  // must be unique and not collide with the structured keys.
  const errors: string[] = []
  for (let i = 0; i < colleagues.length; i++) {
    const c = colleagues[i]
    if (c.email && !EMAIL_RE.test(c.email)) {
      errors.push(`Colleague #${i + 1}: email "${c.email}" looks invalid`)
    }
  }
  const extraKeys = extras.map((e) => e.key.trim()).filter(Boolean)
  const dupes = extraKeys.filter((k, i) => extraKeys.indexOf(k) !== i)
  if (dupes.length > 0) {
    errors.push(`Duplicate extra-field keys: ${[...new Set(dupes)].join(', ')}`)
  }
  const collisions = extraKeys.filter((k) => KNOWN_KEYS.has(k))
  if (collisions.length > 0) {
    errors.push(`Extra-field keys collide with reserved keys: ${collisions.join(', ')}`)
  }

  const save = useMutation({
    mutationFn: () => clients.update(client.id, { context: built }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const isDirty =
    JSON.stringify(canonicalize(built)) !==
    JSON.stringify(canonicalize(client.context ?? {}))

  const updateColleague = (i: number, patch: Partial<Colleague>) => {
    setColleagues((prev) => prev.map((c, j) => j === i ? { ...c, ...patch } : c))
  }
  const removeColleague = (i: number) => {
    setColleagues((prev) => prev.filter((_, j) => j !== i))
  }
  const addColleague = () => {
    setColleagues((prev) => [...prev, { name: '', email: '', role: '', notes: '' }])
  }
  const updateExtra = (i: number, patch: Partial<ExtraField>) => {
    setExtras((prev) => prev.map((e, j) => j === i ? { ...e, ...patch } : e))
  }
  const removeExtra = (i: number) => {
    setExtras((prev) => prev.filter((_, j) => j !== i))
  }
  const addExtra = () => setExtras((prev) => [...prev, { key: '', value: '' }])

  return (
    <div className="mt-6 me-card p-5">
      <div className="me-section-h">Client context</div>
      <div className="text-[12px] me-text-mute mb-4 leading-snug">
        The firm-level JSON injected into every prompt step as{' '}
        <span className="me-mono">{'{{client_context}}'}</span>. Fields below
        are common keys; the model sees all of them plus anything in "Other
        fields". The runner also extracts <span className="me-mono">{'{{firm_name}}'}</span>{' '}
        and <span className="me-mono">{'{{colleagues}}'}</span> as separate variables.
      </div>

      {/* Common keys ─────────────────────────────────── */}
      <div className="space-y-3">
        <div>
          <label className="block text-[11.5px] font-semibold me-text mb-1">
            firm_name
          </label>
          <input
            type="text"
            value={firmName}
            onChange={(e) => setFirmName(e.target.value)}
            placeholder={`(default: "${client.name}")`}
            className="me-input"
          />
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold me-text mb-1">
            description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="1-3 sentences about what the firm does."
            rows={3}
            className="me-input"
            style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold me-text mb-1">
            notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything else worth flagging to the model."
            rows={3}
            className="me-input"
            style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>
      </div>

      {/* Colleagues ─────────────────────────────────── */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11.5px] font-semibold me-text">
            colleagues
            <span className="ml-1 me-text-mute font-normal">
              ({colleagues.length} {colleagues.length === 1 ? 'entry' : 'entries'})
            </span>
          </label>
          <button onClick={addColleague} className="me-btn me-btn-ghost text-[11px]">
            <Plus size={10} /> Add colleague
          </button>
        </div>
        {colleagues.length === 0 && (
          <div className="text-[11.5px] me-text-mute italic">
            No colleagues — click "Add colleague" to define one.
          </div>
        )}
        <div className="space-y-2">
          {colleagues.map((c, i) => {
            const emailInvalid = c.email && !EMAIL_RE.test(c.email)
            return (
              <div
                key={i}
                className="grid grid-cols-[1fr_1.4fr_1fr_1.6fr_auto] gap-2 items-start"
              >
                <input
                  type="text"
                  value={c.name}
                  onChange={(e) => updateColleague(i, { name: e.target.value })}
                  placeholder="Name"
                  className="me-input text-[12px]"
                />
                <input
                  type="email"
                  value={c.email}
                  onChange={(e) => updateColleague(i, { email: e.target.value })}
                  placeholder="email@firm.com"
                  className="me-input text-[12px]"
                  style={emailInvalid ? { borderColor: 'var(--me-urgent)' } : undefined}
                />
                <input
                  type="text"
                  value={c.role}
                  onChange={(e) => updateColleague(i, { role: e.target.value })}
                  placeholder="Role"
                  className="me-input text-[12px]"
                />
                <input
                  type="text"
                  value={c.notes}
                  onChange={(e) => updateColleague(i, { notes: e.target.value })}
                  placeholder="Notes"
                  className="me-input text-[12px]"
                />
                <button
                  onClick={() => removeColleague(i)}
                  className="me-btn me-btn-ghost"
                  title="Remove this colleague"
                  aria-label="Remove colleague"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Extra free-form fields ─────────────────────────── */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11.5px] font-semibold me-text">
            Other fields
            <span className="ml-2 text-[10.5px] me-text-mute font-normal">
              free-form keys — values try to JSON-parse; otherwise stored as a string
            </span>
          </label>
          <button onClick={addExtra} className="me-btn me-btn-ghost text-[11px]">
            <Plus size={10} /> Add field
          </button>
        </div>
        {extras.length === 0 && (
          <div className="text-[11.5px] me-text-mute italic">
            No extras. Use these for anything outside the structured fields above
            (e.g. <span className="me-mono">practice_areas</span>,{' '}
            <span className="me-mono">style_preferences</span>).
          </div>
        )}
        <div className="space-y-2">
          {extras.map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-start">
              <input
                type="text"
                value={e.key}
                onChange={(ev) => updateExtra(i, { key: ev.target.value })}
                placeholder="key"
                className="me-input me-mono text-[12px]"
              />
              <input
                type="text"
                value={e.value}
                onChange={(ev) => updateExtra(i, { value: ev.target.value })}
                placeholder='value (string, or JSON like ["a","b"])'
                className="me-input text-[12px]"
              />
              <button
                onClick={() => removeExtra(i)}
                className="me-btn me-btn-ghost"
                title="Remove this field"
                aria-label="Remove field"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Validation + save ──────────────────────────────── */}
      {errors.length > 0 && (
        <div className="mt-4 text-[11.5px] text-[color:var(--me-urgent)] space-y-0.5">
          {errors.map((m, i) => <div key={i}>⚠ {m}</div>)}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending || errors.length > 0}
          className={isDirty && errors.length === 0 ? 'me-btn me-btn-primary' : 'me-btn'}
        >
          <Save size={12} />
          {save.isPending ? 'Saving…' : 'Save client context'}
        </button>
        {isDirty && (
          <button
            onClick={() => {
              setFirmName(initial.firmName)
              setDescription(initial.description)
              setColleagues(initial.colleagues)
              setNotes(initial.notes)
              setExtras(initial.extras)
            }}
            className="me-btn"
            disabled={save.isPending}
          >
            Discard
          </button>
        )}
        {save.isSuccess && !isDirty && (
          <span className="me-mono text-[11px] text-[color:var(--me-fresh)]">saved</span>
        )}
        {save.error && (
          <span className="text-[12px] text-[color:var(--me-urgent)]">
            {(save.error as Error).message}
          </span>
        )}
      </div>

      <JsonPreview json={preview === '{}' ? '(empty)' : preview} />
    </div>
  )
}

// Same Copy/Copied affordance the artifact viewer uses (BodyPreview
// header). 1.5s feedback window then back to "Copy".
function JsonPreview({ json }: { json: string }) {
  const [copied, setCopied] = useState(false)
  const doCopy = () => {
    if (!navigator.clipboard) return
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => { /* clipboard denied — silent */ })
  }
  return (
    <div className="mt-5">
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[11px] font-semibold me-text-mute uppercase tracking-wider">
          Live preview — JSON the AI will see
        </div>
        <button
          onClick={doCopy}
          className="me-btn me-btn-outline"
          title="Copy the JSON to clipboard"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="me-mono text-[11px] me-text whitespace-pre-wrap break-words p-3 rounded border me-border bg-[color:var(--me-surface)] max-h-96 overflow-auto">
        {json}
      </pre>
    </div>
  )
}
